import { useState } from 'react';
import { toast } from 'react-toastify';
import { AppSidebar } from '../../components/AppSidebar/AppSidebar';
import { useCurrentUser } from '../../features/auth/api';
import {
  usePendingApplications,
  useApplicationDetail,
  useRevealField,
  useApproveApplication,
  useRequestInfo,
  useDenyApplication,
  useOrgTiers,
  useOrgReps,
  openApplicationPdf,
  PendingApplicationRow,
} from '../../features/customer-applications-admin/api';
import { creditLineLabel } from '../../features/customer-application/applicationForm/types';
import './customer-applications.scss';

export default function CustomerApplicationsPage() {
  const { data, isLoading } = usePendingApplications();
  const pending = data?.applications ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);

  return (
    <div className="layout">
      <AppSidebar />
      <div className="card ca-page">
        <div className="ca-head">
          <h2>Customer applications</h2>
          <span className="ca-count">{pending.length} awaiting review</span>
        </div>

        <div className="ca-grid">
          <div className="ca-queue">
            {isLoading ? (
              <div className="apply-note">Loading…</div>
            ) : pending.length === 0 ? (
              <div className="apply-note">No pending customer applications.</div>
            ) : (
              pending.map((a) => (
                <QueueRow
                  key={a.id}
                  app={a}
                  selected={a.id === selectedId}
                  onClick={() => setSelectedId(a.id)}
                />
              ))
            )}
          </div>
          <div className="ca-detail">
            {selectedId ? (
              <ReviewPanel id={selectedId} onClosed={() => setSelectedId(null)} />
            ) : (
              <div className="apply-note">Select an application to review.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Shared with the applicant wizard so the two can't drift.
const creditLabel = (v: string | null) => creditLineLabel(v) || '—';

function QueueRow({
  app,
  selected,
  onClick,
}: {
  app: PendingApplicationRow;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button className={`ca-qrow${selected ? ' sel' : ''}`} onClick={onClick}>
      <span className="ca-qrow__name">{app.legalName}</span>
      <span className="ca-qrow__sub">
        {[app.city, app.state].filter(Boolean).join(', ')} · {app.region} ·{' '}
        {app.isPrepay ? 'Prepay' : `${creditLabel(app.creditLine)} requested`}
      </span>
      <span className={`ca-pill ca-pill--${app.status === 'NEEDS_INFO' ? 'warn' : 'info'}`}>
        {app.status === 'NEEDS_INFO' ? 'Waiting on customer' : 'Processing'}
      </span>
    </button>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="ca-sec">
      <h3>{title}</h3>
      {children}
    </div>
  );
}
function KV({ k, v }: { k: string; v: any }) {
  return (
    <div className="ca-kv">
      <dt>{k}</dt>
      <dd>
        {v === null || v === undefined || v === '' ? <span className="apply-muted">—</span> : v}
      </dd>
    </div>
  );
}

function ReviewPanel({ id, onClosed }: { id: string; onClosed: () => void }) {
  const { data, isLoading } = useApplicationDetail(id);
  const reveal = useRevealField(id);
  const approve = useApproveApplication(id);
  const requestInfo = useRequestInfo(id);
  const deny = useDenyApplication(id);
  const { data: tiers } = useOrgTiers();
  const { data: reps } = useOrgReps();
  const { data: me } = useCurrentUser();
  // Only the owner picks the rep, and not themselves; admins keep the current one.
  const canEditSalesRep = me?.type === 'ORG_OWNER';
  const hiddenSalesRepUid = canEditSalesRep ? me.firebaseUid : null;

  const [tab, setTab] = useState<'approve' | 'info' | 'deny'>('approve');
  const [tierId, setTierId] = useState('');
  const [salesRepUserId, setSalesRepUserId] = useState('');
  const [creditLimit, setCreditLimit] = useState('');
  const [note, setNote] = useState('');
  const [infoMsg, setInfoMsg] = useState('');
  const [denyReason, setDenyReason] = useState('');
  const [productIds, setProductIds] = useState<string[] | null>(null);
  const [revealed, setRevealed] = useState<Record<string, string>>({});

  if (isLoading || !data?.application) return <div className="apply-note">Loading…</div>;
  const a = data.application;
  const chosenProducts = productIds ?? a.products.map((p) => p.id);

  const doReveal = async (key: string, kind: 'ssn', principalId?: string) => {
    try {
      const res = await reveal.mutateAsync({ kind, principalId });
      setRevealed((r) => ({ ...r, [key]: res.value }));
    } catch {
      toast.error('Could not reveal the value.');
    }
  };

  const onApprove = async () => {
    try {
      const res = await approve.mutateAsync({
        tierId: tierId || undefined,
        salesRepUserId: salesRepUserId || undefined,
        creditLimitCents: a.isPrepay || !creditLimit ? null : Math.round(Number(creditLimit) * 100),
        productIds: chosenProducts,
        note: note || undefined,
      });
      toast.success('Approved and activated.');
      // Surface the one Blume outcome that needs admin action: the connection
      // lacks the new credit-limit permission, so the limit won't sync until reconnect.
      if (res?.creditLimitPush === 'insufficient_scope') {
        toast.info('Reconnect Blume to let the approved credit limit sync to QuickBooks.');
      }
      onClosed();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Approve failed.');
    }
  };

  const closed = a.status === 'APPROVED' || a.status === 'DENIED';

  return (
    <div className="ca-review">
      <div className="ca-review__head">
        <div>
          <h2>{a.legalName}</h2>
          <div className="ca-chips">
            <span className="ca-chip">{a.region} region</span>
            <span className="ca-chip">{a.businessType}</span>
            {a.isPrepay && <span className="ca-chip">Prepay</span>}
            {a.hasBankruptcy && <span className="ca-chip ca-chip--bad">Bankruptcy disclosed</span>}
            <span className="ca-chip">{a.status}</span>
          </div>
        </div>
        <button className="apply-btn apply-btn--ghost" onClick={() => openApplicationPdf(id)}>
          Download PDF
        </button>
      </div>

      <div className="ca-cols">
        <div className="ca-sections">
          <Section title="Company background">
            <dl>
              <KV k="Legal name" v={a.legalName} />
              <KV k="DBA" v={a.dba} />
              <KV k="Entity" v={a.entityType} />
              <KV
                k="Incorporated"
                v={[a.yearIncorporated, a.stateOfIncorporation].filter(Boolean).join(', ')}
              />
              <KV k="EIN" v={a.ein} />
              <KV
                k="Office"
                v={[a.office.line1, a.office.city, a.office.state, a.office.zip]
                  .filter(Boolean)
                  .join(', ')}
              />
              <KV k="Phone" v={a.office.phone} />
            </dl>
          </Section>

          <Section title="Account owner">
            <dl>
              <KV k="Name" v={`${a.owner.firstName ?? ''} ${a.owner.lastName ?? ''}`} />
              <KV k="Email" v={`${a.owner.email}${a.owner.emailVerified ? ' ✓' : ''}`} />
              <KV k="Mobile" v={a.owner.mobile} />
              <KV
                k="Heard about us"
                v={[a.owner.heardAboutUs, a.owner.heardAboutUsDetail].filter(Boolean).join(' · ')}
              />
            </dl>
          </Section>

          <Section title="Billing">
            <dl>
              <KV k="Contact" v={a.billing.contact} />
              <KV k="Email" v={a.billing.email} />
              <KV k="Phone" v={a.billing.phone} />
            </dl>
          </Section>

          <Section title="Owners / principals">
            {a.principals.map((p) => (
              <dl key={p.id}>
                <KV
                  k="Name"
                  v={`${p.fullName}${p.title ? `, ${p.title}` : ''}${p.percentOwned ? ` (${p.percentOwned}%)` : ''}`}
                />
                <KV k="Home" v={p.home} />
                {!a.isPrepay && (
                  <KV
                    k="SSN"
                    v={
                      revealed[`ssn:${p.id}`] ? (
                        <span className="ca-num">{revealed[`ssn:${p.id}`]}</span>
                      ) : (
                        <>
                          <span className="ca-num">•••-••-{p.ssnLast4 ?? '????'}</span>{' '}
                          <button
                            className="apply-btn apply-btn--ghost ca-reveal"
                            onClick={() => doReveal(`ssn:${p.id}`, 'ssn', p.id)}
                          >
                            Reveal
                          </button>
                        </>
                      )
                    }
                  />
                )}
              </dl>
            ))}
            {!a.isPrepay && (
              <div className="apply-lock">
                Full SSNs are visible only to credit staff, and every view is logged.
              </div>
            )}
          </Section>

          <Section title="Credit request">
            <dl>
              <KV k="Requested" v={a.isPrepay ? 'Prepay (no credit)' : creditLabel(a.creditLine)} />
              <KV k="Annual volume" v={a.annualTonsRange} />
              {!a.isPrepay && (
                <KV
                  k="Bankruptcy"
                  v={a.hasBankruptcy ? `Yes — ${a.bankruptcyExplanation}` : 'No'}
                />
              )}
            </dl>
          </Section>

          {!a.isPrepay && a.bank && (
            <Section title="Bank reference">
              <dl>
                <KV k="Bank" v={a.bank.name} />
                <KV k="Address" v={a.bank.address} />
                <KV k="Phone" v={a.bank.phone} />
                <KV k="Contact" v={a.bank.contact} />
                <KV k="Account type" v={a.bank.accountType} />
              </dl>
            </Section>
          )}

          {!a.isPrepay && (
            <Section title="Trade references">
              {a.tradeReferenceFileUrl && (
                <div style={{ marginBottom: 8 }}>
                  <a href={a.tradeReferenceFileUrl} target="_blank" rel="noreferrer">
                    See attached reference sheet
                  </a>
                </div>
              )}
              {a.tradeReferences.map((r, i) => (
                <dl key={i}>
                  <KV
                    k={`Reference ${i + 1}`}
                    v={`${r.companyName} · ${r.contactName ?? ''} · ${r.phone ?? ''} · ${r.email ?? ''}`}
                  />
                </dl>
              ))}
              {!a.tradeReferences.length && !a.tradeReferenceFileUrl && (
                <span className="apply-muted">None provided</span>
              )}
            </Section>
          )}

          <Section title="Delivery locations">
            {a.locations.map((l, i) => (
              <dl key={i}>
                <KV
                  k={l.name || `Location ${i + 1}`}
                  v={`${[l.line1, l.city, l.state, l.zip].filter(Boolean).join(', ')}${l.deliveryNotes ? ` · ${l.deliveryNotes}` : ''}`}
                />
              </dl>
            ))}
          </Section>

          <Section title="Team members">
            {a.teamMembers.length === 0 ? (
              <span className="apply-muted">None</span>
            ) : (
              a.teamMembers.map((m, i) => (
                <dl key={i}>
                  <KV k={m.name} v={`${m.email} · ${m.role}`} />
                </dl>
              ))
            )}
          </Section>

          <Section title="Products & tax">
            <dl>
              <KV k="Interested in" v={a.products.map((p) => p.name).join(', ')} />
              <KV k="Tax-exempt" v={a.taxExempt ? `Yes (${a.resale?.state ?? ''})` : 'No'} />
              {a.taxExempt && a.resale && (
                <>
                  <KV k="Business type" v={a.resale.sellerType} />
                  <KV k="Registration #" v={a.resale.registrationNumber} />
                  <KV k="In the business of" v={a.resale.inBusinessOf} />
                  <KV k="Materials" v={a.resale.description} />
                  <KV
                    k="Resale certificate"
                    v={
                      <button
                        className="apply-btn apply-btn--ghost ca-reveal"
                        onClick={() => openApplicationPdf(id, 'resale-pdf')}
                      >
                        Download generated certificate
                      </button>
                    }
                  />
                </>
              )}
            </dl>
          </Section>

          <Section title="Signed terms">
            <dl>
              <KV
                k="Signed by"
                v={`${a.signed.signer ?? ''}${a.signed.signerTitle ? `, ${a.signed.signerTitle}` : ''}`}
              />
              <KV
                k="Personal guarantee"
                v={a.isPrepay ? 'Not required' : a.signed.agreedPersonalGuarantee ? 'Yes' : 'No'}
              />
              <KV
                k="Signed"
                v={
                  a.signed.signedAt
                    ? `${new Date(a.signed.signedAt).toLocaleString()} · IP ${a.signed.signedIp ?? ''}`
                    : '—'
                }
              />
            </dl>
          </Section>
        </div>

        {/* decision rail */}
        <aside className="ca-rail">
          {closed ? (
            <div className="ca-panel">
              <h3>{a.status === 'APPROVED' ? 'Approved' : 'Denied'}</h3>
              <p className="apply-muted">
                {a.status === 'APPROVED'
                  ? 'This account is active. The owner and team got login invites.'
                  : a.decision.deniedReason}
              </p>
            </div>
          ) : (
            <div className="ca-panel">
              <h3>Decision</h3>
              <div className="ca-tabs">
                {(['approve', 'info', 'deny'] as const).map((t) => (
                  <button key={t} aria-pressed={tab === t} onClick={() => setTab(t)}>
                    {t === 'approve' ? 'Approve' : t === 'info' ? 'Need info' : 'Deny'}
                  </button>
                ))}
              </div>

              {tab === 'approve' && (
                <>
                  <label className="ca-field">
                    <span>Tier</span>
                    <select value={tierId} onChange={(e) => setTierId(e.target.value)}>
                      <option value="">No tier</option>
                      {(tiers ?? []).map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="ca-field">
                    <span>Assigned rep</span>
                    <select
                      value={salesRepUserId}
                      onChange={(e) => setSalesRepUserId(e.target.value)}
                      disabled={!canEditSalesRep}
                    >
                      <option value="">{a.salesRepUserId ? 'Keep current' : 'Unassigned'}</option>
                      {(reps ?? [])
                        .filter((r) => r.uid !== hiddenSalesRepUid)
                        .map((r) => (
                          <option key={r.uid} value={r.uid}>
                            {r.name}
                          </option>
                        ))}
                    </select>
                  </label>
                  {!a.isPrepay && (
                    <label className="ca-field">
                      <span>Approved credit limit ($)</span>
                      <input
                        type="number"
                        value={creditLimit}
                        onChange={(e) => setCreditLimit(e.target.value)}
                      />
                    </label>
                  )}
                  <fieldset className="ca-fieldset">
                    <legend>Requested product prices</legend>
                    {a.products.map((p) => (
                      <label key={p.id} className="apply-check">
                        <input
                          type="checkbox"
                          checked={chosenProducts.includes(p.id)}
                          onChange={(e) =>
                            setProductIds(
                              e.target.checked
                                ? [...chosenProducts, p.id]
                                : chosenProducts.filter((x: string) => x !== p.id)
                            )
                          }
                        />
                        <span>{p.name}</span>
                      </label>
                    ))}
                  </fieldset>
                  <label className="ca-field">
                    <span>Internal note (optional)</span>
                    <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                  </label>
                  <button
                    className="apply-btn apply-btn--primary"
                    disabled={approve.isPending}
                    onClick={onApprove}
                  >
                    {approve.isPending ? 'Approving…' : 'Approve and activate'}
                  </button>
                </>
              )}

              {tab === 'info' && (
                <>
                  <label className="ca-field">
                    <span>What do you need from them?</span>
                    <textarea
                      rows={4}
                      value={infoMsg}
                      onChange={(e) => setInfoMsg(e.target.value)}
                    />
                  </label>
                  <button
                    className="apply-btn apply-btn--primary"
                    disabled={requestInfo.isPending || !infoMsg.trim()}
                    onClick={async () => {
                      try {
                        await requestInfo.mutateAsync(infoMsg.trim());
                        toast.success('Request sent.');
                        onClosed();
                      } catch {
                        toast.error('Failed to send.');
                      }
                    }}
                  >
                    Send request
                  </button>
                </>
              )}

              {tab === 'deny' && (
                <>
                  <label className="ca-field">
                    <span>Reason (sent to the applicant)</span>
                    <textarea
                      rows={4}
                      value={denyReason}
                      onChange={(e) => setDenyReason(e.target.value)}
                    />
                  </label>
                  <button
                    className="apply-btn apply-btn--danger"
                    disabled={deny.isPending || !denyReason.trim()}
                    onClick={async () => {
                      try {
                        await deny.mutateAsync(denyReason.trim());
                        toast.success('Application denied.');
                        onClosed();
                      } catch {
                        toast.error('Failed to deny.');
                      }
                    }}
                  >
                    Deny application
                  </button>
                </>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
